import styled, { css, keyframes } from 'styled-components';

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(10, 187, 135, 0.3); }
  50% { box-shadow: 0 0 40px rgba(10, 187, 135, 0.6); }
`;

const MEDAL_COLORS = {
  1: { primary: '#FFD700', secondary: '#FFA500', glow: 'rgba(255, 215, 0, 0.4)' },
  2: { primary: '#C0C0C0', secondary: '#A0A0A0', glow: 'rgba(192, 192, 192, 0.4)' },
  3: { primary: '#CD7F32', secondary: '#A0522D', glow: 'rgba(205, 127, 50, 0.4)' }
} as const;

export const PodiumWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 16px;
  margin-bottom: 48px;
  width: 100%;
  max-width: 800px;

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

export const PodiumSlot = styled.div<{ position: 1 | 2 | 3 }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  order: ${({ position }) => (position === 1 ? 1 : position === 2 ? 0 : 2)};
`;

export const PodiumCard = styled.div<{ position: 1 | 2 | 3 }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ position }) => (position === 1 ? '32px 28px 24px' : '24px 20px 20px')};
  border-radius: 24px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.04) 100%);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  width: ${({ position }) => (position === 1 ? '240px' : '200px')};
  transition: transform 0.3s ease;

  ${({ position }) =>
    position === 1 &&
    css`
      border-color: rgba(10, 187, 135, 0.3);
      animation: ${float} 3s ease-in-out infinite, ${glow} 2s ease-in-out infinite;
    `}

  &:hover {
    transform: scale(1.03);
  }

  @media (max-width: 768px) {
    width: ${({ position }) => (position === 1 ? '180px' : '150px')};
    padding: ${({ position }) => (position === 1 ? '24px 16px 20px' : '20px 12px 16px')};
  }
`;

export const MedalBadge = styled.div<{ position: 1 | 2 | 3 }>`
  position: absolute;
  top: -18px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 800;
  background: linear-gradient(
    135deg,
    ${({ position }) => MEDAL_COLORS[position].primary},
    ${({ position }) => MEDAL_COLORS[position].secondary}
  );
  color: #0e1215;
  box-shadow: 0 4px 12px ${({ position }) => MEDAL_COLORS[position].glow};
`;

export const AvatarWrapper = styled.div<{ position: 1 | 2 | 3 }>`
  width: ${({ position }) => (position === 1 ? '80px' : '64px')};
  height: ${({ position }) => (position === 1 ? '80px' : '64px')};
  border-radius: 50%;
  overflow: hidden;
  margin-bottom: 12px;
  border: 3px solid ${({ position }) => MEDAL_COLORS[position].primary};
  box-shadow: 0 0 16px ${({ position }) => MEDAL_COLORS[position].glow};
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    width: ${({ position }) => (position === 1 ? '64px' : '52px')};
    height: ${({ position }) => (position === 1 ? '64px' : '52px')};
  }
`;

export const AvatarPlaceholder = styled.div<{ position: 1 | 2 | 3 }>`
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    ${({ position }) => MEDAL_COLORS[position].primary},
    ${({ position }) => MEDAL_COLORS[position].secondary}
  );
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ position }) => (position === 1 ? '28px' : '22px')};
  font-weight: 700;
  color: #0e1215;
`;

export const Name = styled.span<{ position: 1 | 2 | 3 }>`
  font-size: ${({ position }) => (position === 1 ? '16px' : '14px')};
  font-weight: 700;
  color: #ffffff;
  text-align: center;
  margin-bottom: 4px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Score = styled.span<{ position: 1 | 2 | 3 }>`
  font-size: ${({ position }) => (position === 1 ? '28px' : '22px')};
  font-weight: 800;
  background: linear-gradient(
    135deg,
    ${({ position }) => MEDAL_COLORS[position].primary},
    ${({ position }) => MEDAL_COLORS[position].secondary}
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 8px;
`;

export const StatsRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

export const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

export const StatValue = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
`;

export const StatLabel = styled.span`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const PodiumBase = styled.div<{ position: 1 | 2 | 3 }>`
  width: 100%;
  height: ${({ position }) => (position === 1 ? '100px' : position === 2 ? '70px' : '50px')};
  margin-top: 12px;
  border-radius: 12px 12px 0 0;
  background: linear-gradient(
    180deg,
    ${({ position }) => MEDAL_COLORS[position].primary}33,
    ${({ position }) => MEDAL_COLORS[position].primary}11
  );
  border-top: 2px solid ${({ position }) => MEDAL_COLORS[position].primary}66;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 12px;
  font-size: 24px;
  font-weight: 800;
  color: ${({ position }) => MEDAL_COLORS[position].primary};

  @media (max-width: 768px) {
    height: ${({ position }) => (position === 1 ? '80px' : position === 2 ? '55px' : '40px')};
  }
`;
